import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function absoluteMediaUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/media/")) return `${BACKEND_URL}${url}`;
  return url;
}

/**
 * Single image field with upload + URL input fallback.
 * Props: value (string), onChange(url), testId
 */
export function ImageField({ value, onChange, testId, label }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const doUpload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Immagine caricata");
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Upload fallito");
    } finally { setBusy(false); }
  };

  return (
    <div>
      {value ? (
        <div className="relative inline-block border border-hair bg-white p-2 mb-3" data-testid={`${testId}-preview`}>
          <img src={absoluteMediaUrl(value)} alt="anteprima" className="w-32 h-32 object-cover" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 bg-ink text-bone p-1 hover:bg-gold transition-colors"
            data-testid={`${testId}-clear`}
            aria-label="Rimuovi"
          >
            <X size={12} />
          </button>
        </div>
      ) : null}

      <div className="flex flex-col sm:flex-row gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`${testId}-file`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) doUpload(f);
            e.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] border border-ink text-xs tracking-[0.14em] uppercase hover:bg-ink hover:text-bone transition-colors disabled:opacity-60"
          data-testid={`${testId}-upload`}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {busy ? "Caricamento…" : (value ? "Sostituisci foto" : "Carica foto")}
        </button>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="oppure incolla URL"
          className="flex-1 border border-hair px-3 py-2 text-sm rounded-none focus:outline-none focus:border-ink"
          data-testid={`${testId}-url`}
        />
      </div>
      {label && <p className="text-[11px] text-taupe mt-2">{label}</p>}
    </div>
  );
}

/**
 * Multiple images field (portfolio gallery, product gallery)
 */
export function MultiImageField({ value = [], onChange, testId }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const uploadMany = async (files) => {
    setBusy(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      } catch (err) {
        toast.error(`Errore su ${file.name}`);
      }
    }
    if (uploaded.length) {
      onChange([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} foto caricate`);
    }
    setBusy(false);
  };

  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div>
      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3" data-testid={`${testId}-list`}>
          {value.map((src, i) => (
            <div key={i} className="relative border border-hair bg-white p-1 group">
              <img src={absoluteMediaUrl(src)} alt="" className="w-full h-24 object-cover" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 bg-ink text-bone p-1 opacity-90 hover:bg-gold transition-colors"
                data-testid={`${testId}-remove-${i}`}
                aria-label="Rimuovi"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        multiple
        accept="image/*"
        className="hidden"
        data-testid={`${testId}-file`}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) uploadMany(files);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] border border-ink text-xs tracking-[0.14em] uppercase hover:bg-ink hover:text-bone transition-colors disabled:opacity-60"
        data-testid={`${testId}-upload`}
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
        {busy ? "Caricamento…" : "Aggiungi foto"}
      </button>
      <p className="text-[11px] text-taupe mt-2">Puoi selezionare più foto contemporaneamente. Max 8 MB ciascuna. Formati: JPG, PNG, WEBP.</p>
    </div>
  );
}
